import { Fragment, useEffect, useState } from "react";
import { CrossSvg, FilterSvg, ListSettingSvg, PlusSvg, ReloadSvg, SearchSvg, VerticalDotsSvg } from "../../assets/svgs";
import { AddUpdateAcsParameterModal, BadgeComponent, ButtonComponent, DropDownComponent, EmptyTableComponent, FiltersComponent, IconButtonComponent, LoaderComponent, PaginationComponent, TableComponent, ViewParametersModal } from "../../components";
import { getAllAcsParameters, getAllAcsParametersName, getDsConfigurationList, getMessageInclusionsList, getMessageVersionsList, postAddUpdateParameter } from "../../redux/slices/acs-parameters-slice";
import { useDispatch, useSelector } from "react-redux";
import { handleDateTimeFormat, isValidISODateTime } from "../../helpers/dateTimeFormat";
import { AuthPageContainer, AuthPageHead } from "../../styles/global-styles";
import styled from "styled-components";

const ParameterValueContainer = styled.div`
    i {
        color: var(--theme-color-primary);
        font-size: 12px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const AcsParameters = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.loginUserReducer);
    // const { institutions } = useSelector((state) => state.institutionsListReducer);
    const { schemes } = useSelector((state) => state.schemesListReducer);
    const { loading, acsParametersData, pagination, acsParametersNameList, messageInclusionsList, messageVersionsList, dsConfigurationList } = useSelector((state) => state.acsParametersReducer);

    const initialPagination = {
        currentPage: 0,
        pageSize: 20,
    };
    const initialOrderBy = {
        sortColumn: "createOnDateTime",
        isAsce: false,
    };
    const initialSearch = {
        acsParametersId: '',
        parameterValue: '',
        // institutionId: '',
        schemeId: '',
        messageVersionId: '',
        messageInclusionId: '',
        dsConfigurationId: '',
    };

    const initialForm = {
        // institutionId: '',
        institutionName: '',
        schemeId: '',
        schemeName: '',
        acsParametersId: '',
        acsParameterName: '',
        parameterValue: '',
        dsConfigurationId: '',
        dsConfigurationName: '',
        messageInclusionId: '',
        messageInclusion: '',
        messageVersionId: '',
        messageVersion: '',
        effectiveDateTime: '',
    };

    const initialStates = {
        data: [],
        pagination: initialPagination,
        orderBy: initialOrderBy,
        search: initialSearch,
        selected: null,
        isDropdownOpen: false,
        isModalOpen: false,
        isUpdate: false,
        isView: false,
        isFiltersOpen: false,
        selectedLabel: null,
        isDetailedView: null,
    };

    const [states, setStates] = useState(initialStates);
    const [form, setForm] = useState(initialForm);

    const defaultParams = { pagination: states.pagination, orderBy: states.orderBy, search: states.search };

    const getAllAcsParametersData = async (updatedParams) => {
        if (user && updatedParams) {
            try {
                await Promise.all([
                    dispatch(getAllAcsParameters({ user, ...updatedParams })),
                    dispatch(getAllAcsParametersName(user)),
                    dispatch(getMessageInclusionsList(user)),
                    dispatch(getMessageVersionsList(user)),
                    dispatch(getDsConfigurationList(user)),
                ]);
            } catch (error) {
                console.error("One or more API calls failed", error);
            }
        }
    };

    const handleReload = () => {
        getAllAcsParametersData(defaultParams);
    };

    const handlePagination = (e) => {
        const params = {
            ...defaultParams,
            pagination: {
                ...defaultParams.pagination,
                currentPage: e
            }
        };
        getAllAcsParametersData(params);
    };

    const handleChangeFilters = (e) => {
        const { name, value, type, checked } = e.target
        setStates((old) => ({
            ...old,
            search: ({
                ...old.search,
                [name]: ['checkbox', 'radio'].includes(type) ? checked : value
            })
        }));
    };

    const handleSelectRow = (row) => {
        if (row === states.selected && states.isDropdownOpen) {
            setStates((old) => ({ ...old, isDropdownOpen: false, selected: null }));
        } else {
            setStates((old) => ({ ...old, isDropdownOpen: true, selected: row }));
        }
    };

    const handleCloseDropdown = () => {
        setStates((old) => ({ ...old, isDropdownOpen: false }));
    };

    const handleToggleFilters = () => {
        setStates((old) => ({ ...old, search: initialSearch, isFiltersOpen: !states.isFiltersOpen }));
    };

    const handleModalOpen = (e) => {
        if (['view', 'edit'].includes(e)) {
            const { id, institution, schemeId, acsParametersId, parameterValue, dsConfigurationId, messageInclusionId, messageVersionId, effectiveDateTime } = states?.selected;

            setForm({
                id,
                // institutionId: institution?.id || '',
                institutionName: institution?.descr || '',
                schemeId: schemeId?.id || '',
                schemeName: schemeId?.name || '',
                acsParametersId: acsParametersId?.id || '',
                acsParameterName: acsParametersId?.parameterName || '',
                parameterValue,
                dsConfigurationId: dsConfigurationId?.id || '',
                dsConfigurationName: dsConfigurationId?.dsName || '',
                messageInclusionId: messageInclusionId?.id || '',
                messageInclusion: messageInclusionId?.name || '',
                messageVersionId: messageVersionId?.id || '',
                messageVersion: messageVersionId?.version || '',
                effectiveDateTime: handleDateTimeFormat({ dateTime: effectiveDateTime, iso: true }) || ''
            });

            if (e === 'view') {
                setStates((old) => ({ ...old, isView: true, isModalOpen: true }));
            } else if (e === 'edit') {
                setStates((old) => ({ ...old, isUpdate: true, isModalOpen: true }));
            }
        } else {
            setStates((old) => ({ ...old, isModalOpen: true }));
            setForm(initialForm);
        }
    };

    const handleModalClose = () => {
        setStates((old) => ({ ...old, isModalOpen: false, selected: null, isUpdate: false, isView: false, isDetailedView: null }));
    };

    const handleFormSubmit = async (payload) => {
        if (user && payload) {
            const isValidIsoDateTime = isValidISODateTime(payload?.effectiveDateTime);

            const formattedDateTime = isValidIsoDateTime
                ? handleDateTimeFormat({ dateTime: payload?.effectiveDateTime, normal: true })
                : payload?.effectiveDateTime

            const updatedPayload = await {
                ...payload,
                effectiveDateTime: formattedDateTime
            }

            dispatch(postAddUpdateParameter({ user, body: updatedPayload, update: states.isUpdate })).then((action) => {
                if (action?.payload?.status === 201 || action?.payload?.status === 200) {
                    handleModalClose();
                    getAllAcsParametersData(defaultParams);
                }
            });
        }
    };

    const handleFiltersSubmit = (e) => {
        e.preventDefault();
        const params = {
            ...defaultParams,
            search: states.search,
            pagination: initialPagination,
        };
        getAllAcsParametersData(params);
    };

    const handleViewMore = ({ label, body }) => {
        if (body && Object.entries(JSON.parse(body)).length > 0) {
            const dataArray = Object.entries(JSON.parse(body)).map(([key, value]) => ({
                label: key,
                value: JSON.stringify(value)
            }));
            setStates((old) => ({ ...old, isDetailedView: dataArray, selectedLabel: label }));
        }
    };

    const actions = (effectiveDateTime) => {
        const actionsList = [
            { label: 'View', action: () => handleModalOpen('view') },
        ];

        if (effectiveDateTime) {
            const today = new Date();
            const effectiveDate = new Date(handleDateTimeFormat({ dateTime: effectiveDateTime, iso: true }));

            if (effectiveDate > today) {
                actionsList.push({ label: 'Edit', action: () => handleModalOpen('edit') });
            }
        }

        return actionsList;
    };

    useEffect(() => {
        getAllAcsParametersData(defaultParams);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (acsParametersData && Array.isArray(acsParametersData)) {
            setStates((old) => ({ ...old, data: acsParametersData, pagination: pagination }));
        }
    }, [acsParametersData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown')) {
                handleCloseDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const searchFields = [
        {
            label: 'Parameter',
            type: 'select',
            options: acsParametersNameList,
            name: 'acsParametersId',
            value: states.search.acsParametersId,
        },
        {
            label: 'Parameter value',
            type: 'text',
            placeholder: 'Search by parameter value',
            name: 'parameterValue',
            value: states.search.parameterValue,
        },
        // {
        //     label: 'Participant',
        //     type: 'select',
        //     options: institutions?.map(e => ({ ...e, value: e?.id })),
        //     name: 'institutionId',
        //     value: states.search.institutionId,
        // },
        {
            label: 'Message type',
            type: 'select',
            options: messageInclusionsList,
            name: 'messageInclusionId',
            value: states.search.messageInclusionId,
        },
        {
            label: 'Message version',
            type: 'select',
            options: messageVersionsList,
            name: 'messageVersionId',
            value: states.search.messageVersionId,
        },
        {
            label: 'Directory server',
            type: 'select',
            options: dsConfigurationList,
            name: 'dsConfigurationId',
            value: states.search.dsConfigurationId,
        },
        {
            label: 'Scheme',
            type: 'select',
            options: schemes?.map(e => ({ label: e?.name, value: e?.id })),
            name: 'schemeId',
            value: states.search.schemeId,
        },
        {
            type: 'action',
            actions: [
                { label: 'Cancel', icon: CrossSvg, type: 'button', outline: true, action: handleToggleFilters },
                { label: 'Search', icon: SearchSvg, type: 'submit', action: () => { } }
            ],
        }
    ];

    // TABLE COLUMNS SETUP AS PER DATA
    const columns = [
        {
            field: 'parameterName', headerName: 'Parameter Name', width: 140,
            valueGetter: (row) => row?.acsParametersId?.parameterName
        },
        {
            field: 'parameterValue', headerName: 'Parameter Value', width: 120,
            valueGetter: (row) => {
                return (
                    <>
                        {
                            row?.parameterValue &&
                            <>
                                {
                                    row?.parameterValue.length > 35 ?
                                        <ParameterValueContainer>
                                            <p>{row?.parameterValue.substring(0, 35) + '...'}</p>
                                            <i onClick={() => handleViewMore({ label: row?.acsParametersId?.parameterName, body: row?.parameterValue })}>View more</i>
                                        </ParameterValueContainer>
                                        :
                                        <p>{row?.parameterValue}</p>
                                }
                            </>
                        }
                    </>
                );
            }
        },
        // {
        //     field: 'institution', headerName: 'Participant', width: 120,
        //     valueGetter: (row) => row?.institution?.descr
        // },
        {
            field: 'messageInclusionId', headerName: 'Message Type', width: 120, align: 'center',
            valueGetter: (row) => row?.messageInclusionId?.name
        },
        {
            field: 'messageVersionId', headerName: 'Message Version', width: 140, align: 'center',
            valueGetter: (row) => {
                return (
                    <>
                        {
                            row?.messageVersionId &&
                            <BadgeComponent label={row?.messageVersionId?.version} type="success" />
                        }
                    </>
                )
            }
        },
        {
            field: 'dsConfigurationId', headerName: 'Directory server', width: 120,
            valueGetter: (row) => row?.dsConfigurationId?.dsName
        },
        {
            field: 'schemeId', headerName: 'Scheme', width: 120, align: 'center',
            valueGetter: (row) => {
                return (
                    <>
                        {
                            row?.schemeId &&
                            <BadgeComponent label={row?.schemeId?.name} />
                        }
                    </>
                )
            }
        },
        { field: 'effectiveDateTime', headerName: 'Effective At', width: 170, align: 'center' },
        {
            field: 'action',
            headerName: 'Action',
            sortable: false,
            width: 100,
            align: 'right',
            sticky: 'right',
            valueGetter: (row) => {
                return (
                    <div className="action-dropdown" style={{ position: 'relative', float: 'right' }}>
                        <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow(row)} />
                        <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row} close={handleCloseDropdown} actions={actions(row?.effectiveDateTime)} />
                    </div>
                )
            },
        },
    ];

    return (
        <AuthPageContainer $heightfull>
            {/* PAGE HEAD */}
            <AuthPageHead>
                <div className="left">
                    <h1>ACS Parameters</h1>
                    <IconButtonComponent icon={ReloadSvg} outline size="small" disabled={loading} onClick={handleReload} className={loading && 'box-spin'} />
                </div>

                <div className="right">
                    <ButtonComponent icon={PlusSvg} label="Add ACS Parameter" size="small" disabled={loading} onClick={handleModalOpen} />
                    <IconButtonComponent icon={FilterSvg} outline onClick={handleToggleFilters} />
                </div>
            </AuthPageHead>

            {/* FILTER */}
            {
                searchFields && searchFields.length > 0 &&
                <FiltersComponent open={states.isFiltersOpen} searchFields={searchFields} handleChange={handleChangeFilters} handleSubmit={handleFiltersSubmit} />
            }

            {/* LOADING STATE */}
            {
                loading &&
                <LoaderComponent />
            }

            {/* EMPTY STATE */}
            {
                !loading && states.data && states.data.length === 0 &&
                <EmptyTableComponent text='No data found!' />
            }

            {/* PAGE CONTENT */}
            {
                !loading && states.data && states.data.length > 0 &&
                <Fragment>
                    <TableComponent rows={states.data} columns={columns} icon={ListSettingSvg} />
                    <PaginationComponent data={states.pagination} handleChange={(e) => handlePagination(e)} />
                </Fragment>
            }

            {/* MODAL */}
            {
                states.isModalOpen &&
                <AddUpdateAcsParameterModal data={form} label={`${states.isUpdate ? 'Update' : states.isView ? 'View' : 'Add'} Parameter`} viewOnly={states.isView} update={states.isUpdate} close={handleModalClose} handleFormSubmit={handleFormSubmit} />
            }
            {
                states.isDetailedView &&
                <ViewParametersModal data={states?.isDetailedView} label={`${states?.selectedLabel}'s Parameter Value`} close={handleModalClose} />
            }
        </AuthPageContainer>
    );
};

export default AcsParameters;